import { defineMessages } from 'react-intl';

export default defineMessages({
  addUser: {
    id: 'page.admin.users.adduser',
    defaultMessage: 'Add User',
  },
  searchFilter: {
    id: 'page.admin.users.searchfilter',
    defaultMessage: 'Search Filter',
  },
  firstName: {
    id: 'page.admin.users.firstname',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'page.admin.users.lastname',
    defaultMessage: 'Last Name',
  },
  enabledOnly: {
    id: 'page.admin.users.enabledonly',
    defaultMessage: 'Enabled Only',
  },
  searchPlaceholder: {
    id: 'page.admin.users.searchplaceholder',
    defaultMessage: 'Search by email',
  },
  pageTitle: {
    id: 'page.admin.users.title',
    defaultMessage: 'Users',
  },
  loadingMessage: {
    id: 'page.admin.users.loading',
    defaultMessage: 'Loading users...',
  },
  clickToView: {
    id: 'page.admin.users.clicktoview',
    defaultMessage: 'Click to view',
  },
  enabled: {
    id: 'page.admin.users.enabled',
    defaultMessage: 'Enabled',
  },
  disabled: {
    id: 'page.admin.users.disabled',
    defaultMessage: 'Disabled',
  },
  role: {
    id: 'page.admin.users.role',
    defaultMessage: 'Role',
  },
  keys: {
    id: 'page.admin.users.keys',
    defaultMessage: 'Keys',
  },
  status: {
    id: 'page.admin.users.status',
    defaultMessage: 'Status',
  },
  options: {
    id: 'page.admin.users.options',
    defaultMessage: 'Options',
  },
  settings: {
    id: 'page.admin.users.settings',
    defaultMessage: 'Settings',
  },
  notificationEmail: {
    id: 'page.admin.users.notificationemail',
    defaultMessage: 'Notification Email',
  },
  allowUserGroupAssets: {
    id: 'page.admin.users.allowusergroupassets',
    defaultMessage: 'Access to other user assets',
  },
  allowUserGroupAssetsHint: {
    id: 'page.admin.users.allowusergroupassetshint',
    defaultMessage:
      'When enabled, this user can access assets uploaded by all other users',
  },

  // --- Contributor seats in the invite modal (plan-structure v2 Phase 6) ---
  // A role the account has no seat for is shown but DISABLED, and these explain
  // why: a greyed-out option with no explanation reads as a bug, and the server
  // would reject the invite anyway (structured plan_upgrade_required 403).
  seatsNoContributorSeats: {
    id: 'page.admin.users.seatsnocontributorseats',
    defaultMessage:
      'All {quota, plural, one {# contributor seat is} other {# contributor seats are}} in use, '
      + 'so new members can only be added as USER/VIEWER — free, read-only access.',
  },
  // A trial has exactly one contributor seat and cannot buy more (there is no
  // card on file to prorate against), so it gets its own copy and a different
  // CTA: pick a plan, not buy a seat.
  seatsNoContributorSeatsTrial: {
    id: 'page.admin.users.seatsnocontributorseatstrial',
    defaultMessage:
      'Your trial includes one contributor seat, which you are using. You can still add '
      + 'USER/VIEWER members — free, read-only access.',
  },
  seatsBuyCta: {
    id: 'page.admin.users.seatsbuycta',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'Add contributor seats — ${price}/month each',
  },
  seatsChoosePlanCta: {
    id: 'page.admin.users.seatschooseplancta',
    defaultMessage: 'Choose a plan to add contributors',
  },
  seatsOwnerOnly: {
    id: 'page.admin.users.seatsowneronly',
    defaultMessage: 'Contact your subscription owner ({email}) to add contributor seats.',
  },
  submit: {
    id: 'page.admin.users.submit',
    defaultMessage: 'Submit',
  },
  cancel: {
    id: 'page.admin.users.cancel',
    defaultMessage: 'Cancel',
  },
  newUserModalTitle: {
    id: 'page.admin.users.newusermodaltitle',
    defaultMessage: 'New User',
  },
  userAddedSuccessfully: {
    id: 'page.admin.users.useraddedsuccessfully',
    defaultMessage: 'User added successfully, please wait for the user to be processed by our systems...',
  },
  userEditedSuccessfully: {
    id: 'page.admin.users.usereditedsuccessfully',
    defaultMessage: 'User {email} updated successfully',
  },
  editingUserModalTitle: {
    id: 'page.admin.users.editingusermodaltitle',
    defaultMessage: 'Editing {email}',
  },
  newPassword: {
    id: 'page.admin.users.newpassword',
    defaultMessage: 'New Password',
  },
  keysModalTitle: {
    id: 'page.admin.users.keysmodaltitle',
    defaultMessage: `Viewing {email}'s keys`,
  },
  pendingSignup: {
    id: 'page.admin.users.pendingsignup',
    defaultMessage: 'Pending Signup',
  },
  resendSignup: {
    id: 'page.admin.users.resendsignup',
    defaultMessage: 'Resend Signup Email',
  },
  resendSignupSuccess: {
    id: 'page.admin.users.resendsignupsuccess',
    defaultMessage: 'Signup email resent successfully to {email}',
  },

  // --- Seat lifecycle (plan-structure v2 Phase 7a) ---
  removed: {
    id: 'page.admin.users.removed',
    defaultMessage: 'Removed',
  },
  showRemoved: {
    id: 'page.admin.users.showremoved',
    defaultMessage: 'Show removed members',
  },
  removeMember: {
    id: 'page.admin.users.removemember',
    defaultMessage: 'Remove from team',
  },
  revokeInvite: {
    id: 'page.admin.users.revokeinvite',
    defaultMessage: 'Revoke invite',
  },
  reassignInvite: {
    id: 'page.admin.users.reassigninvite',
    defaultMessage: 'Reassign invite',
  },
  restoreMember: {
    id: 'page.admin.users.restoremember',
    defaultMessage: 'Restore',
  },
  removeMemberTitle: {
    id: 'page.admin.users.removemembertitle',
    defaultMessage: 'Remove {email} from the team?',
  },
  revokeInviteTitle: {
    id: 'page.admin.users.revokeinvitetitle',
    defaultMessage: 'Revoke the invitation for {email}?',
  },
  removeMemberBody: {
    id: 'page.admin.users.removememberbody',
    defaultMessage:
      'They lose access immediately and their seat is freed straight away. '
      + 'Nothing is deleted — they keep ownership of everything they uploaded and '
      + 'will show as a removed member. You can restore them later if a seat is free.',
  },
  revokeInviteBody: {
    id: 'page.admin.users.revokeinvitebody',
    defaultMessage:
      'The invitation link stops working and the seat is freed straight away. '
      + 'You can restore the invite later if a seat is free.',
  },
  removeMemberSuccess: {
    id: 'page.admin.users.removemembersuccess',
    defaultMessage: '{email} was removed from the team. Their seat is now free.',
  },
  restoreMemberSuccess: {
    id: 'page.admin.users.restoremembersuccess',
    defaultMessage: '{email} was restored to the team.',
  },
  reassignInviteTitle: {
    id: 'page.admin.users.reassigninvitetitle',
    defaultMessage: 'Reassign this invitation',
  },
  reassignInviteBody: {
    id: 'page.admin.users.reassigninvitebody',
    defaultMessage:
      '{email} has not accepted yet, so the seat can be handed to someone else. '
      + 'The old invitation link stops working and a new one is emailed to the new address.',
  },
  reassignInviteSuccess: {
    id: 'page.admin.users.reassigninvitesuccess',
    defaultMessage: 'Invitation reassigned — a new invite was sent to {email}.',
  },
  newEmail: {
    id: 'page.admin.users.newemail',
    defaultMessage: 'New email address',
  },
  noSeatToRestore: {
    id: 'page.admin.users.noseattorestore',
    defaultMessage: 'No free seat — upgrade your plan or free a seat to restore this member.',
  },
  // Multi-group strip + reveal (moved from the Subscription page). Shown only
  // when the subscription spans more than one group — the cross-group seat
  // context. The list itself opens in GroupsModal via "Click to view".
  groupsStripIntro: {
    id: 'page.admin.users.groupsstripintro',
    defaultMessage:
      'Contributor seats are shared across the {count} groups on your subscription.',
  },
  groupsModalTitle: {
    id: 'page.admin.users.groupsmodaltitle',
    defaultMessage: 'Groups on this subscription',
  },
  groupMembers: {
    id: 'page.admin.users.groupmembers',
    defaultMessage: '{count, plural, one {# member} other {# members}}',
  },
  groupMeta: {
    id: 'page.admin.users.groupmeta',
    defaultMessage: 'Group ID {id} · created {created}',
  },
});
